import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { GALLERY } from "../data/content";

import fachada1 from "../assets/gallery/newgallery/fachada.jpg";
import areaJuegos1 from "../assets/gallery/newgallery/area-juegos-1.jpg";
import areaJuegos2 from "../assets/gallery/newgallery/area-juegos-2.jpg";
import areaJuegos3 from "../assets/gallery/newgallery/area-juegos-3.jpg";
import areaJuegos4 from "../assets/gallery/newgallery/area-juegos-4.jpg";
import interiores1 from "../assets/gallery/newgallery/interiores-1.jpg";
import interiores2 from "../assets/gallery/newgallery/interiores-2.jpg";
import interiores3 from "../assets/gallery/newgallery/interiores-3.jpg";
import areaComida1 from "../assets/gallery/newgallery/area-comida-1.jpg";
import areaComida2 from "../assets/gallery/newgallery/area-comida-2.jpg";
import areaComida3 from "../assets/gallery/newgallery/area-comida-3.jpg";

import salaLounge from "../assets/gallery/newgallery2/1.jpeg";
import barraSnacks1 from "../assets/gallery/newgallery2/2.jpeg";
import paletasMenu from "../assets/gallery/newgallery2/3.jpeg";
import paletasServicio from "../assets/gallery/newgallery2/4.jpeg";
import salonGeneral from "../assets/gallery/newgallery2/5.jpeg";
import futbolito from "../assets/gallery/newgallery2/6.jpeg";
import arcades1 from "../assets/gallery/newgallery2/7.jpeg";
import paleta from "../assets/gallery/newgallery2/8.jpeg";
import barraSnacks2 from "../assets/gallery/newgallery2/9.jpeg";
import arcades2 from "../assets/gallery/newgallery2/10.jpeg";

// Respaldo: si /api/gallery no responde o aún no hay fotos en Azure, se muestra esta galería fija.
const IMAGES = {
  fachada: [fachada1],
  areaJuegos: [areaJuegos1, areaJuegos2, areaJuegos3, areaJuegos4, arcades2, arcades1, futbolito],
  interiores: [interiores1, interiores2, interiores3, salonGeneral, salaLounge],
  areaComida: [
    areaComida1,
    areaComida2,
    areaComida3,
    barraSnacks2,
    barraSnacks1,
    paletasMenu,
    paletasServicio,
    paleta,
  ],
};

const FALLBACK = GALLERY.map((c) => ({ ...c, images: IMAGES[c.key] }));

function GalleryCard({ cat, images, idx, onNav, onOpen }) {
  const many = images.length > 1;

  return (
    <div className="gal-card" onClick={() => onOpen(cat.key, idx)}>
      <div className="gal-frame">
        <div className="gal-img" style={{ backgroundImage: `url(${images[idx]})` }} />
        {many && (
          <>
            <button
              type="button"
              className="gal-arrow prev"
              aria-label="Foto anterior"
              onClick={(e) => {
                e.stopPropagation();
                onNav(cat.key, -1);
              }}
            >
              ‹
            </button>
            <button
              type="button"
              className="gal-arrow next"
              aria-label="Foto siguiente"
              onClick={(e) => {
                e.stopPropagation();
                onNav(cat.key, 1);
              }}
            >
              ›
            </button>
            <div className="gal-dots">
              {images.map((_, i) => (
                <span
                  key={i}
                  className={"dot" + (i === idx ? " active" : "")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onNav(cat.key, i - idx);
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <div className="gal-card-meta">
        <span className="ttl">{cat.title}</span>
        <span className="kicker">{cat.kicker}</span>
      </div>
    </div>
  );
}

export default function Galeria() {
  // null mientras carga, para no descargar las fotos de respaldo si la API sí responde.
  const [cats, setCats] = useState(null);
  const [idxByCat, setIdxByCat] = useState({});
  const [open, setOpen] = useState(null); // { key, idx } | null

  useEffect(() => {
    let alive = true;
    fetch("/api/gallery")
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data) => {
        const list = (data.categories || [])
          .filter((c) => c.images && c.images.length > 0)
          .map((c) => ({ key: `c${c.id}`, title: c.title, kicker: c.kicker, images: c.images }));
        if (alive) setCats(list.length > 0 ? list : FALLBACK);
      })
      .catch(() => alive && setCats(FALLBACK));
    return () => {
      alive = false;
    };
  }, []);

  const imagesOf = (key) => (cats || []).find((c) => c.key === key)?.images || [];

  const nav = (key, dir) => {
    setIdxByCat((prev) => {
      const n = imagesOf(key).length;
      const next = ((prev[key] || 0) + dir + n) % n;
      return { ...prev, [key]: next };
    });
  };

  const openAt = (key, idx) => setOpen({ key, idx });

  const navLightbox = (dir) => {
    setOpen((o) => {
      if (!o) return o;
      const n = imagesOf(o.key).length;
      return { key: o.key, idx: (o.idx + dir + n) % n };
    });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!open) return;
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowLeft") navLightbox(-1);
      if (e.key === "ArrowRight") navLightbox(1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cats]);

  const cat = open ? cats.find((c) => c.key === open.key) : null;
  const openImages = cat ? cat.images : null;

  return (
    <section id="galeria" className="band cream-soft" data-screen-label="05 Galeria">
      <div className="band-bg" />
      <div className="scatter" />
      <div className="tear-top tear-sage-top" />
      <div className="tear-bottom tear-sage-bottom" />

      <div className="wrap">
        <div className="sec-head">
          <h2 className="sec-title">Galería</h2>
        </div>

        <div className="gal-grid">
          {cats === null
            ? GALLERY.map((c) => (
                <div key={c.key} className="gal-card" aria-hidden="true">
                  <div className="gal-frame" />
                </div>
              ))
            : cats.map((c) => (
                <GalleryCard
                  key={c.key}
                  cat={c}
                  images={c.images}
                  idx={idxByCat[c.key] || 0}
                  onNav={nav}
                  onOpen={openAt}
                />
              ))}
        </div>
      </div>

      {/* Portal a <body>: dentro de .band (z-index propio) el lightbox quedaba debajo de las secciones siguientes y del footer. */}
      {createPortal(
      <div
        className={"lightbox" + (open ? " show" : "")}
        onClick={(e) => e.target === e.currentTarget && setOpen(null)}
      >
        <button className="close" onClick={() => setOpen(null)}>✕</button>
        <div className="stage">
          {open && (
            <div
              className="ph"
              style={{ backgroundImage: `url(${openImages[open.idx]})` }}
            />
          )}
          {open && openImages.length > 1 && (
            <>
              <button
                type="button"
                className="lb-arrow prev"
                aria-label="Foto anterior"
                onClick={() => navLightbox(-1)}
              >
                ‹
              </button>
              <button
                type="button"
                className="lb-arrow next"
                aria-label="Foto siguiente"
                onClick={() => navLightbox(1)}
              >
                ›
              </button>
            </>
          )}
          <div className="label">
            {open ? `✿ ${cat.title} · ${open.idx + 1}/${openImages.length}` : "—"}
          </div>
        </div>
      </div>,
      document.body
      )}
    </section>
  );
}
